import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService, SessionStore } from '@core';
import { MenuHeaderTopic, UiOption } from '@ui';
import { map, tap } from 'rxjs';
import { DialogInputPasswordComfirmComponent } from './components/dialog-input-password/dialog-input-password.component';
import { UserMangeModalComponent } from './components/user-mange-modal/user-mange-modal.component';
import { UserService } from './services/user.service';
import { roleOptions, statusOptions } from './types/user-data';
import { UserManageAction, UserSearchValue } from './types/user-manage.types';
import { DeleteUserReq, UserDto } from './types/user.dto';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
})
export class UserManagementComponent implements OnInit {
  titleTopic: MenuHeaderTopic = {
    title: 'view_value.user_management',
    subtitle: 'view_value.user_list',
    iconUrl: 'assets/shared/icons/people.svg',
  };
  private allUsers: UserDto[] = [];

  userList: UserDto[] = [];
  roleOptions: string[] = roleOptions;
  statusOptions: UiOption<string>[] = statusOptions;
  currentFilter: UserSearchValue = { keyword: '', role: null, status: null };

  loadUserList() {
    const req$ = this.userSerivce.getListUser().pipe(
      map((res) => res?.users ?? []),
      tap((users) => {
        this.allUsers = users;

        // this.roleOptions = this.buildOptions(users, (u) => u.role);
        // this.statusOptions = this.buildStatusOptions(users);

        this.applyFilter(this.currentFilter);
      }),
    );
    this.dialogService.withLoading(req$, { disableClose: true }).subscribe({
      error: (err: any) => {
        this.dialogService.error('โหลดข้อมูลไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
      },
    });
  }

  onSearch(filter: UserSearchValue) {
    this.currentFilter = filter;
    this.applyFilter(this.currentFilter);
  }

  onReset() {
    this.currentFilter = { keyword: '', role: null, status: null };
    this.applyFilter(this.currentFilter);
  }

  private applyFilter(filter: UserSearchValue) {
    const kw = (filter.keyword ?? '').trim().toLowerCase();
    const role = filter.role;
    const status = (filter.status ?? null)?.toString().toLowerCase();

    this.userList = this.allUsers.filter((u) => {
      const matchKeyword =
        !kw ||
        `${u.first_name ?? ''} ${u.last_name ?? ''}`
          .toLowerCase()
          .includes(kw) ||
        (u.email ?? '').toLowerCase().includes(kw);

      const uStatus = (u.status ?? '').trim().toLowerCase();
      const matchRole = !role || u.role === role;
      const matchStatus = !status || uStatus === status;

      return matchKeyword && matchRole && matchStatus;
    });
  }

  private buildOptions<T>(
    rows: T[],
    pick: (row: T) => string | null | undefined,
  ): string[] {
    const uniq = new Set<string>();
    for (const r of rows) {
      const v = (pick(r) ?? '').trim();
      if (v) uniq.add(v);
    }
    return Array.from(uniq).sort((a, b) => a.localeCompare(b));
  }

  private buildStatusOptions(users: UserDto[]): UiOption<string>[] {
    const uniq = new Set<string>();
    for (const u of users) {
      const raw = (u.status ?? '').trim().toLowerCase();

      if (raw) uniq.add(raw);
    }

    const labelMap: Record<string, string> = {
      active: 'ใช้งาน',
      inactive: 'ไม่ใช้งาน',
    };

    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({
        value: v,
        label: labelMap[v] ?? v,
      }));
  }

  openCreate() {
    this.dialog
      .open(UserMangeModalComponent, {
        width: 'fit-content',
        panelClass: ['user-manage-dialog'],
        data: {
          mode: 'create',
          roleOptions: this.roleOptions,
          statusOptions: this.statusOptions,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;
        const req$ = this.userSerivce.createUser(res);

        this.dialogService
          .withLoading(req$, { disableClose: true })
          .subscribe((res) => {
            if (res.meta.response_code === 20000) {
              this.loadUserList();
              this.dialogService.message({
                title: 'โปรดคัดลอกรหัสนี้เก็บไว้',
                type: 'secret',
                secretValue: `${res.meta.response_data.generated_password}`,
              });
            } else {
              this.dialogService.error(
                'สร้างผู้ใช้ไม่สำเร็จ',
                'กรุณาลองใหม่อีกครั้ง',
              );
            }
          });
      });
  }

  openEdit(row: UserDto) {
    this.dialog
      .open(UserMangeModalComponent, {
        width: 'fit-content',
        panelClass: ['user-manage-dialog'],
        disableClose: true,
        data: {
          mode: 'edit',
          user: row,
          roleOptions: this.roleOptions,
          statusOptions: this.statusOptions,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;

        const req$ = this.userSerivce.editUser(res);

        this.dialogService
          .withLoading(req$, { disableClose: true })
          .subscribe((res) => {
            if (res.meta.response_code === 20000) {
              this.loadUserList();
              this.dialogService.success(
                'แก้ไขข้อมูลผู้ใช้งานสำเร็จ',
                'อัปเดตข้อมูลเรียบร้อยแล้ว',
              );
            } else {
              this.dialogService.error(
                'แก้ไขข้อมูลผู้ใช้ไม่สำเร็จ',
                'กรุณาลองใหม่อีกครั้ง',
              );
            }
          });
      });
  }

  openDelete(row: UserDto) {
    this.dialog
      .open(DialogInputPasswordComfirmComponent, {
        width: 'fit-content',
        panelClass: ['user-manage-dialog'],
        data: {
          title: 'ยืนยันการลบผู้ใช้งาน',
          detail: `คุณต้องการลบข้อมูลผู้ใช้งาน ${row.email} หรือไม่ ?`,
        },
      })
      .afterClosed()
      .subscribe((password: string) => {
        if (!password) return;
        const superAdminId = this.sessionStore.getUserId();

        const payload: DeleteUserReq = {
          super_admin_id: Number(superAdminId),
          super_admin_password: password,
          target_user_id: row.id,
        };
        const req$ = this.userSerivce.deleteUser(payload);

        this.dialogService.withLoading(req$, { disableClose: true }).subscribe({
          next: (res) => {
            if (res.meta.response_code === 20000) this.loadUserList();
            else
              this.dialogService.error(
                'ลบข้อมูลไม่สำเร็จ',
                'กรุณาลองใหม่อีกครั้ง',
              );
          },
          error: (err) => {
            console.error(err);
            this.dialogService.error(
              'ลบข้อมูลไม่สำเร็จ',
              'กรุณาลองใหม่อีกครั้ง',
            );
          },
        });
      });
  }

  onRowAction(e: { action: UserManageAction; row: UserDto }) {
    if (e.action === 'edit') this.openEdit(e.row);
    else if (e.action === 'delete') this.openDelete(e.row);
  }

  constructor(
    private userSerivce: UserService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private sessionStore: SessionStore,
  ) {}

  ngOnInit(): void {
    this.loadUserList();
  }
}
